import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EntityCreator } from '../../data/entity-creator'
import { PredefinedSpec } from '../../data/predefined-actors'
import { assertDefined, expectOneById, extractOneFromList, jsonClone, nextOrdinal, sortByOrdinal } from '../../util/utils'
import { Actor, FightData, Spell, SpellAssignment } from '../models'

type NewFightPayload = {
    name: string
}

type ViewTimelineState = {
    storedFights: FightData[]
    editingFight: FightData
}

const initialState: ViewTimelineState = {
    storedFights: [],

    // TODO: This should be a proper dummy-object, it'll be swapped on
    // load anyway
    editingFight: (null as any)
}

const viewTimelineSlice = createSlice({
    name: "view-timeline",
    initialState: initialState,
    reducers: {

        selectFight: (state, { payload }: PayloadAction<{ fightId: string }>) => {

            const fightData = state.storedFights
                .find(fightData => fightData.fight.id === payload.fightId)

            assertDefined(fightData)
            state.editingFight = jsonClone(fightData)
        },

        createFight: (state, { payload }: PayloadAction<NewFightPayload>) => {
            const fight = EntityCreator.createFight({ name: payload.name })
            const actor = EntityCreator.createActor(({
                fightId: fight.id,
                name: 'Boss',
                isBoss: true,
                ordinal: 0
            }))

            const spell = EntityCreator.createSpell({
                actorId: actor.id,
                cooldown: 60,
                duration: 10,
                name: 'Ability',
                ordinal: 0,
                ramp: 0
            })

            const fightData: FightData = {
                fight,
                assignments: [],
                actors: [actor],
                spells: [spell]
            }

            state.storedFights.push(fightData)
            state.editingFight = jsonClone(fightData)
        },

        cloneFight: (state) => {
            const clonedFight = EntityCreator.cloneFightData(state.editingFight)

            state.storedFights.push(clonedFight)
            state.editingFight = jsonClone(clonedFight)
        },

        cloneGivenFight: (state, { payload }: PayloadAction<{ fightData: FightData }>) => {
            
            const clonedFight = EntityCreator.cloneFightData(payload.fightData)
            clonedFight.fight.name = `${clonedFight.fight.name} (imported)`

            state.storedFights.push(clonedFight)
            state.editingFight = jsonClone(clonedFight)
        },

        deleteFight: (state) => {

            if (state.storedFights.length <= 1) {
                return
            }

            state.storedFights = state.storedFights
                .filter(fd => fd.fight.id !== state.editingFight.fight.id)

            const newSelectedFight = state.storedFights[0]
            state.editingFight = jsonClone(newSelectedFight)
        },

        setFightName: (state, { payload }: PayloadAction<{ name: string }>) => {
            state.editingFight.fight.name = payload.name
        },

        saveFightChanges: (state) => {
            const { editingFight, storedFights } = state

            const rest = storedFights.filter(fightData => fightData.fight.id !== editingFight.fight.id)

            state.storedFights = [
                ...rest,
                {
                    ...editingFight,
                    fight: {
                        ...editingFight.fight,
                        updatedAt: new Date().toISOString(),
                    }
                }
            ]
        },

        updateActor: (state, { payload }: PayloadAction<{ actorId: string; updateFields: Partial<Actor> }>) => {

            const { match, restOfList } = extractOneFromList(
                state.editingFight.actors,
                (act) => act.id === payload.actorId
            )

            state.editingFight.actors = [
                ...restOfList,
                {
                    ...match,
                    ...payload.updateFields
                }
            ]

            state.editingFight.actors = sortByOrdinal(state.editingFight.actors)
        },

        updateSpells: (state, { payload }: PayloadAction<{ spells: Spell[]; deleteSpells: string[] }>) => {

            payload.spells.forEach(spell => {

                const { match, restOfList } = extractOneFromList(state.editingFight.spells, (s) => s.id === spell.id)
                state.editingFight.spells = [
                    ...restOfList,
                    {
                        ...match,
                        ...spell
                    }
                ]
            })

            state.editingFight.spells = state.editingFight.spells
                .filter(s => !payload.deleteSpells.includes(s.id))
            state.editingFight.assignments = state.editingFight.assignments
                .filter(as => !payload.deleteSpells.includes(as.spellId))

            state.editingFight.spells = sortByOrdinal(state.editingFight.spells)
        },

        createActor: (state, { payload }: PayloadAction<{ name: string }>) => {

            const fight = state.editingFight.fight
            const ordinal = nextOrdinal(state.editingFight.actors)

            const actor = EntityCreator.createActor({
                fightId: fight.id,
                isBoss: false,
                name: payload.name,
                ordinal
            })

            state.editingFight.actors.push(actor)
        },

        createActorFull: (state, { payload }: PayloadAction<{ color: string; predefinedSpec: PredefinedSpec }>) => {

            const fight = state.editingFight.fight
            const ordinal = nextOrdinal(state.editingFight.actors)

            const actor = EntityCreator.createActor({
                fightId: fight.id,
                isBoss: false,
                name: payload.predefinedSpec.label,
                ordinal,
                color: payload.color
            })

            const spells = payload.predefinedSpec.spells.map((spell, index) => {
                return EntityCreator.createSpell({
                    actorId: actor.id,
                    ordinal: index,
                    name: spell.name,
                    ramp: spell.ramp,
                    duration: spell.duration,
                    cooldown: spell.cd,
                })
            })

            state.editingFight.actors.push(actor)
            state.editingFight.spells.push(...spells)
        },

        deleteActor: (state, { payload }: PayloadAction<{ actorId: string }>) => {
            const { editingFight } = state

            editingFight.actors = editingFight.actors.filter(actor => actor.id !== payload.actorId)

            const deletedSpells: string[] = []
            editingFight.spells = editingFight.spells.filter(spell => {
                if (spell.actorId !== payload.actorId) {
                    deletedSpells.push(spell.id)
                    return true
                }

                return false
            })

            editingFight.assignments = editingFight.assignments.filter(as => deletedSpells.includes(as.spellId))
        },

        updateAssignmentOffset: (state, { payload }: PayloadAction<{ assignmentId: string; offset: number }>) => {

            const assignment = expectOneById(state.editingFight.assignments, payload.assignmentId)
            assignment.offset = payload.offset
        },

        createNewAssignment: (state, { payload }: PayloadAction<{ spellId: string; offset: number }>) => {

            const assignment = EntityCreator.createAssignment({
                offset: payload.offset,
                spellId: payload.spellId,
            })

            state.editingFight.assignments.push(assignment)
        },

        deleteAssignment: (state, { payload }: PayloadAction<{ assignmentId: string }>) => {

            state.editingFight.assignments = state.editingFight.assignments
                .filter(assign => assign.id !== payload.assignmentId)
        },

        updateAssignmentOverrides: (state, { payload }: PayloadAction<{ spellAssignment: SpellAssignment }>) => {

            const assignment = expectOneById(state.editingFight.assignments, payload.spellAssignment.id)

            assignment.hideTimestamp = payload.spellAssignment.hideTimestamp
            assignment.overrideCooldown = payload.spellAssignment.overrideCooldown
            assignment.overrideDuration = payload.spellAssignment.overrideDuration
            assignment.overrideRamp = payload.spellAssignment.overrideRamp
        },

        cloneActor: (state, { payload }: PayloadAction<{ actorId: string }>) => {

            const { actor, spells, assignments } = EntityCreator
                .cloneActor(state.editingFight, payload.actorId)

            state.editingFight.actors.push(actor)
            state.editingFight.spells.push(...spells)
            state.editingFight.assignments.push(...assignments)

        },

        changeActorOrdinal: (state, { payload }: PayloadAction<{ actorId: string; dir: "up" | "down" }>) => {

            const { match: boss, restOfList: playerActors } = extractOneFromList(
                state.editingFight.actors,
                (a) => a.isBoss
            )

            if (playerActors.length <= 1) {
                return
            }

            const ordinalSorted = sortByOrdinal(playerActors)
            const targetIndex = ordinalSorted.findIndex(actor => actor.id === payload.actorId)
            const targetSpell = ordinalSorted[targetIndex]

            if (payload.dir === "up") {
                if (targetIndex === 0) {
                    return
                }

                const swapTarget = ordinalSorted[targetIndex - 1]

                targetSpell.ordinal--
                swapTarget.ordinal++

            } else {
                if (targetIndex === ordinalSorted.length - 1) {
                    return
                }

                const swapTarget = ordinalSorted[targetIndex + 1]

                targetSpell.ordinal++
                swapTarget.ordinal--
            }

            state.editingFight.actors = sortByOrdinal([
                boss,
                ...ordinalSorted
            ])
        },

    }
})

export const viewTimelineReducer = viewTimelineSlice.reducer
export const ViewTimelineActions = viewTimelineSlice.actions